import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box, Button, Card, Field, Flex, HStack, Heading, Input, Stack, Text, Textarea,
} from '@chakra-ui/react'
import { LuEye, LuCode, LuRotateCcw, LuSend } from 'react-icons/lu'
import { useAuth } from '../contexts/AuthContext'
import { useTranslation } from '../i18n'
import { toaster } from './ui/toaster'
import {
  TEMPLATE_LANGUAGES, emptyCopy, previewHtml, previewSubject, previewText,
  templateFormat, unknownPlaceholders,
} from '../services/emailTemplates'

const LANGUAGE_LABELS = { es: 'Español', en: 'English' }

// A sandboxed iframe, not dangerouslySetInnerHTML: template markup is authored
// by admins and must never execute inside the dashboard.
function Preview({ html }) {
  return (
    <Box borderRadius="md" overflow="hidden" border="1px solid" borderColor="var(--mc-border)" bg="white">
      <iframe
        title="email-preview"
        sandbox=""
        srcDoc={html}
        style={{ width: '100%', height: '340px', border: 'none', background: 'white' }}
      />
    </Box>
  )
}

// A plain-text template's preview: what the guest's share sheet would carry,
// placeholders filled with sample values. Rendered as text, never markup.
function TextPreview({ text }) {
  return (
    <Box
      borderRadius="md"
      border="1px solid"
      borderColor="var(--mc-border)"
      bg="var(--mc-input-bg)"
      p={3}
      fontSize="sm"
      whiteSpace="pre-wrap"
      wordBreak="break-word"
    >
      {text}
    </Box>
  )
}

/**
 * One template's editor: a language pair, subject, HTML body, and a preview.
 * A plain-text template (`spec.format === 'text'`, the reel share caption) gets
 * a single text field and a text preview instead, and no test send — it is not
 * an email. Draft state is local — nothing is written until Save.
 */
export function EmailTemplateEditor({
  templateId,
  spec,
  samples,
  content,
  defaultContent,
  initialLanguage,
  onSave,
  onSendTest,
}) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [lang, setLang] = useState(initialLanguage || TEMPLATE_LANGUAGES[0])
  const [draft, setDraft] = useState(content)
  const [showPreview, setShowPreview] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testTo, setTestTo] = useState(user?.email || '')
  const [sending, setSending] = useState(false)
  const bodyRef = useRef(null)
  const isText = templateFormat(spec) === 'text'
  // The field placeholders are inserted into and the textarea edits.
  const bodyField = isText ? 'text' : 'html'

  // Re-seed when the caller loads a different document (e.g. another event).
  useEffect(() => { setDraft(content) }, [content])

  const copy = useMemo(
    () => draft?.[lang] || emptyCopy(spec),
    [draft, lang, spec]
  )
  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(content),
    [draft, content]
  )
  const unknown = useMemo(
    () => unknownPlaceholders(copy, spec?.placeholders),
    [copy, spec]
  )

  const setField = (field, value) =>
    setDraft((prev) => ({ ...prev, [lang]: { ...prev?.[lang], [field]: value } }))

  // Insert at the caret so a placeholder lands where the admin is typing.
  const insertPlaceholder = (name) => {
    const el = bodyRef.current
    const token = `{{${name}}}`
    const body = copy[bodyField] || ''
    if (!el) return setField(bodyField, `${body}${token}`)
    const { selectionStart: start, selectionEnd: end } = el
    const next = `${body.slice(0, start)}${token}${body.slice(end)}`
    setField(bodyField, next)
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(start + token.length, start + token.length)
    })
  }

  const handleRestore = () => {
    if (!defaultContent) return
    setDraft((prev) => ({ ...prev, [lang]: { ...defaultContent[lang] } }))
  }

  const handleSendTest = async () => {
    try {
      setSending(true)
      // The draft, not the saved copy: the point is to see a change before
      // committing it.
      await onSendTest({ to: testTo.trim(), lang, ...copy })
      toaster.create({ title: t('templates.testSent', { email: testTo.trim() }), type: 'success' })
    } catch (err) {
      toaster.create({ title: t('templates.testFailed'), description: err.message, type: 'error' })
    } finally {
      setSending(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      await onSave(draft)
      toaster.create({ title: t('templates.saved'), type: 'success' })
    } catch (err) {
      toaster.create({ title: t('templates.saveFailed'), description: err.message, type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card.Root bg="var(--mc-bg-surface)" border="1px solid" borderColor="var(--mc-border)">
      <Card.Body>
        <Stack gap={3}>
          <Flex align="center" gap={3} wrap="wrap">
            <Heading size="sm">{t(`templates.name.${templateId}`)}</Heading>
            <Box flex="1" />
            <HStack gap={1}>
              {TEMPLATE_LANGUAGES.map((code) => (
                <Button
                  key={code}
                  size="xs"
                  variant={lang === code ? 'solid' : 'outline'}
                  onClick={() => setLang(code)}
                >
                  {LANGUAGE_LABELS[code] || code}
                </Button>
              ))}
            </HStack>
          </Flex>

          <Text fontSize="xs" color="var(--mc-text-muted)">
            {t(`templates.description.${templateId}`)}
          </Text>

          {!isText && (
            <Field.Root>
              <Field.Label>{t('templates.subject')}</Field.Label>
              <Input
                value={copy.subject}
                onChange={(e) => setField('subject', e.target.value)}
              />
            </Field.Root>
          )}

          <Field.Root>
            <Field.Label>{t(isText ? 'templates.text' : 'templates.body')}</Field.Label>
            {isText ? (
              <Textarea
                ref={bodyRef}
                value={copy.text || ''}
                rows={5}
                onChange={(e) => setField('text', e.target.value)}
              />
            ) : (
              <Textarea
                ref={bodyRef}
                value={copy.html}
                rows={14}
                fontFamily="'JetBrains Mono', monospace"
                fontSize="xs"
                onChange={(e) => setField('html', e.target.value)}
              />
            )}
          </Field.Root>

          <Box>
            <Text fontSize="xs" color="var(--mc-text-muted)" mb={1}>
              {t('templates.placeholders')}
            </Text>
            <Flex gap={2} wrap="wrap">
              {(spec?.placeholders || []).map((name) => (
                <Button
                  key={name}
                  size="xs"
                  variant="outline"
                  fontFamily="'JetBrains Mono', monospace"
                  onClick={() => insertPlaceholder(name)}
                >
                  {`{{${name}}}`}
                </Button>
              ))}
            </Flex>
          </Box>

          {unknown.length > 0 && (
            <Text fontSize="xs" color="orange.300">
              {t('templates.unknownPlaceholders', { names: unknown.join(', ') })}
            </Text>
          )}

          {showPreview && isText && (
            <TextPreview text={previewText(copy.text, samples)} />
          )}

          {showPreview && !isText && (
            <Stack gap={2}>
              <Text fontSize="xs" color="var(--mc-text-muted)">
                {t('templates.subject')}: {previewSubject(copy.subject)}
              </Text>
              <Preview html={previewHtml(copy.html)} />
            </Stack>
          )}

          {onSendTest && !isText && (
            <Flex gap={2} align="flex-end" wrap="wrap">
              <Field.Root flex="1" minW="200px" maxW="320px">
                <Field.Label>{t('templates.testTo')}</Field.Label>
                <Input
                  type="email"
                  size="sm"
                  value={testTo}
                  placeholder="tu@correo.cl"
                  onChange={(e) => setTestTo(e.target.value)}
                />
              </Field.Root>
              <Button
                size="sm"
                variant="outline"
                loading={sending}
                loadingText={t('templates.testSending')}
                disabled={!testTo.trim()}
                onClick={handleSendTest}
              >
                <LuSend />
                {t('templates.testSend')}
              </Button>
              <Text fontSize="xs" color="var(--mc-text-muted)" pb={2}>
                {t('templates.testHelp')}
              </Text>
            </Flex>
          )}

          <Flex gap={2} justify="flex-end" wrap="wrap">
            <Button size="sm" variant="ghost" onClick={() => setShowPreview((v) => !v)}>
              {showPreview ? <LuCode /> : <LuEye />}
              {showPreview ? t('templates.hidePreview') : t('templates.showPreview')}
            </Button>
            {defaultContent && (
              <Button size="sm" variant="outline" onClick={handleRestore}>
                <LuRotateCcw />
                {t('templates.restoreDefault')}
              </Button>
            )}
            <Button
              size="sm"
              colorPalette="brand"
              loading={saving}
              loadingText={t('common.saving')}
              disabled={!dirty}
              onClick={handleSave}
            >
              {t('common.save')}
            </Button>
          </Flex>
        </Stack>
      </Card.Body>
    </Card.Root>
  )
}
